import { useState, useEffect, useCallback, useRef } from 'react'
import { showsTrialUx } from '../utils/offerFlags'
import { studioEvents } from '../utils/metaPixel'
import { API_URL, sb } from '../utils/config'
import { useAuth } from '../context/AuthContext'
import SpotlightTutorial from './SpotlightTutorial'
import merlinSucces from '../assets/Merlin_succes-removebg-preview.png'

/**
 * StudioOnboarding — activation du 1er passage Studio Pro.
 *
 * CONTEXTE METIER : 100% des resilies d'essai n'avaient JAMAIS active le
 * produit (0 scan / 0 action). L'onboarding ne se contente pas de montrer
 * des ecrans : il fait PERCEVOIR la valeur (note /100, 3 formats, format du
 * jour) PUIS pousse a la 1ere action cle (ouvrir un format).
 *
 * VISUEL : on REUTILISE le tuto Merlin a spotlight (SpotlightTutorial) — trou
 * lumineux sur la zone + bulle Merlin violette, etapes, dots. Les etapes
 * pointent les zones de la home Studio (data-tour="...") et la derniere etape
 * declenche l'ouverture du 1er format.
 *
 * 2 briques exportees :
 *  - <StudioOnboarding /> (default) : le tour, montre une fois (localStorage).
 *  - <ActivationChecklist /> : checklist 3 actions en haut de la home.
 */

const ONBOARDED_KEY = 'studio_onboarded_v1'
// v2 : on passe de 3 etapes (score/open/save) a 4 etapes orientees SURFACES
// (score/format/video/account). Bump du key pour repartir propre.
const ACTIVATION_KEY = 'studio_activation_v2'
const COLLAPSED_KEY = 'studio_activation_collapsed'

const ACTIVATION_STEPS = ['score', 'format', 'video', 'account', 'perso', 'save']
const EMPTY_ACTIVATION = { score: false, format: false, video: false, account: false, perso: false, save: false }

// Etat d'activation persiste. 4 etapes orientees surfaces cles :
//  - score   : note /100 vue (auto a l'arrivee)
//  - format  : un format viral ouvert (surface Decouvrir)
//  - video   : une analyse de SA video lancee (surface Analyse)
//  - account : une analyse de SON compte lancee (surface Analyse)
function readActivation() {
  if (typeof window === 'undefined') return { ...EMPTY_ACTIVATION }
  try {
    const raw = localStorage.getItem(ACTIVATION_KEY)
    if (!raw) return { ...EMPTY_ACTIVATION }
    const parsed = JSON.parse(raw)
    return {
      score: !!parsed.score,
      format: !!parsed.format,
      video: !!parsed.video,
      account: !!parsed.account,
      perso: !!parsed.perso,
      save: !!parsed.save,
    }
  } catch {
    return { ...EMPTY_ACTIVATION }
  }
}

function writeActivation(state) {
  if (typeof window === 'undefined') return
  try { localStorage.setItem(ACTIVATION_KEY, JSON.stringify(state)) } catch {}
}

// Helper exporte : marque une etape d'activation faite + emet l'event la 1ere
// fois seulement (idempotent). Utilisable depuis n'importe ou dans Studio Pro.
export function markActivationStep(step) {
  if (!ACTIVATION_STEPS.includes(step)) return
  const cur = readActivation()
  if (cur[step]) return
  const next = { ...cur, [step]: true }
  writeActivation(next)
  try { studioEvents.activationStep({ step }) } catch {}
  try { window.dispatchEvent(new CustomEvent('studio:activation-changed', { detail: next })) } catch {}
}

// ═══════════════════════════════════════════════════════════════
// ETAPES DU TOUR — intro centre + spotlights sur les zones + action finale.
// Les zones absentes (skipIfMissing) sont sautees : le tour s'adapte. La
// derniere etape est centree (pas de target) donc TOUJOURS affichee, avec le
// CTA qui ouvre le 1er format.
// ═══════════════════════════════════════════════════════════════
// ── TEASERS DE VALEUR — aperçus RÉELS de ce qui vit derrière le gating.
// Doctrine (données churn 07/2026) : les résiliés d'essai n'avaient presque
// jamais touché la profondeur (exemples perso, analyse vidéo). Le tuto la
// MONTRE (contenu pré-rendu, non copiable tel quel) pour créer l'envie.
const badgeEssai = (
  <span style={{ display: 'inline-block', fontSize: 9, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', color: '#fff', marginBottom: 8 }}>
    {showsTrialUx() ? "✨ Inclus avec l'essai gratuit" : '✨ Inclus avec Merlin Pro'}
  </span>
)

const ExempleTeaser = (
  <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)', borderRadius: 14, padding: '12px 14px', fontFamily: 'DM Sans,sans-serif' }}>
    {badgeEssai}
    <p style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.95)', margin: '0 0 6px' }}>Exemple adapté à TON compte</p>
    <p style={{ fontSize: 11.5, color: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.95)', fontStyle: 'italic', margin: '0 0 8px', lineHeight: 1.5 }}>« 3 outils que tu ne dois jamais utiliser pour tes reels (le 2e va te surprendre) »</p>
    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}>
      <div>✓ Ouvre sur l'interdiction, sans dire bonjour</div>
      <div>✓ Montre chaque outil 2 secondes, nom + usage</div>
      <div>✓ Termine sur le plus fort des trois</div>
    </div>
  </div>
)

// Fire-and-forget d'un event global (pilotage des étapes en contexte réel).
const fire = (name) => { try { window.dispatchEvent(new CustomEvent(name)) } catch { /* no-op */ } }

// ── ÉTAPES EN CONTEXTE RÉEL (2026-07, demande Antonin) : le tuto n'affiche
// plus des maquettes à côté, il OUVRE la vraie fiche format, déclenche la
// vraie analyse, et pointe l'endroit exact où vivent les exemples perso.
// L'analyse vidéo est démontrée avec une VRAIE fiche (fixture réelle d'un
// créateur) via TourVideoAnalysisDemo (StudioAnalysisTab).
const openFicheStep = {
  target: '[data-tour="fiche-signature"]',
  layout: 'top',
  allowScroll: true,
  waitTarget: 8000,
  skipIfMissing: true,
  onEnter: () => { fire('studio:tour-hide-analysis-demo'); fire('studio:tour-open-first-format') },
  title: "Voilà la fiche d'un format",
  text: "Ta 1re fiche : la vidéo comparée à sa niche. Tu vois ce qui l'a fait décoller, et pourquoi.",
}
const persoInFicheStep = (textePerso) => ({
  target: '[data-tour="fiche-perso"]',
  layout: 'top',
  allowScroll: true,
  waitTarget: 30000,
  skipIfMissing: true,
  onEnter: () => {
    fire('studio:tour-hide-analysis-demo')
    fire('studio:tour-open-first-format') // rouvre si on revient de la démo
    setTimeout(() => fire('studio:tour-load-fiche'), 400)
  },
  title: 'Des exemples écrits pour TOI',
  text: textePerso,
})
const analysisDemoStep = (texteDemo) => ({
  target: '[data-tour="analysis-demo"]',
  layout: 'bottom',
  allowScroll: true,
  waitTarget: 8000,
  skipIfMissing: true,
  onEnter: () => {
    fire('studio:close-format-detail')
    fire('studio:goto-video-analysis') // on montre OÙ ça vit : l'onglet Analyse
    fire('studio:tour-show-analysis-demo')
  },
  title: 'Merlin analyse TES vidéos',
  text: texteDemo,
  tip: "L'exemple est interactif : fais-le défiler, ouvre les sections.",
})

// Nettoyage complet des surfaces ouvertes par le tour (fiche + démo).
const tourCleanup = () => { fire('studio:tour-hide-analysis-demo'); fire('studio:close-format-detail'); fire('studio:goto-home') }

// ═══════════════════════════════════════════════════════════════
// ÉTAPES v2 (2026-07) — mobile d'abord, orientées CONVERSION :
// la note (le miroir) → les formats du jour (la raison de revenir) →
// 2 teasers du contenu gaté (l'envie) → Découvrir → l'action.
// ═══════════════════════════════════════════════════════════════
// (FREE_STEPS[3/4].body sont branchés en bas du fichier, après les teasers)
const STUDIO_STEPS = [
  {
    title: 'Bienvenue dans ton Studio',
    text: "Ton compte est analysé. 30 secondes pour voir comment t'en servir.",
  },
  {
    target: '[data-tour="score"]',
    title: 'Ta note sur 100',
    text: "Ta note : où tu es fort, où tu perds des vues. Objectif : la faire monter.",
    skipIfMissing: true,
  },
  // Le tunnel qui convertit (note → améliorer → analyse) : le tuto le montre
  // au lieu de le cacher. Constat 09/07 : les sessions exposées au tuto
  // cliquaient ce bouton à 15 % contre 38 % sans tuto.
  {
    target: '[data-tour="score-improve"]',
    title: 'Et pour la faire monter ?',
    text: "Ce bouton te donne le plan complet : ce qui te coûte des vues, quoi corriger, axe par axe. Retiens-le.",
    skipIfMissing: true,
  },
  {
    target: '[data-tour="formats"]',
    layout: 'top',
    allowScroll: true,
    title: 'Tes formats du jour 🔥',
    text: "3 nouveaux formats de ta niche chaque jour. Chaque carte s'ouvre sur l'analyse complète.",
    skipIfMissing: true,
  },
  openFicheStep,
  persoInFicheStep("Et c'est ICI, en bas de chaque fiche, que Merlin réécrit le format pour TON compte : le hook exact à dire, les étapes à filmer. Ça donne ça :"),
  analysisDemoStep("On est dans l'onglet Analyse. Tu envoies une de tes vidéos, Merlin te dit pourquoi elle a marché (ou pas) et quoi corriger. Déroule l'exemple, c'est une vraie analyse :"),
  {
    target: '[data-tour="tab-library"]',
    title: 'Des milliers de formats viraux',
    text: "Découvrir : cherche ton sujet, filtre par difficulté.",
    skipIfMissing: true,
    onEnter: tourCleanup,
  },
  {
    title: 'À toi de jouer',
    text: "Ouvre ton 1er format. Demain 7h : nouveau drop.",
    action: 'open-first-format',
    actionLabel: 'Ouvrir mon 1er format →',
    last: true,
    onEnter: tourCleanup,
  },
]


// ═══════════════════════════════════════════════════════════════
// ÉTAPES FREE — l'écran de RÉSULTATS juste après le scan (tous comptes).
// C'est LE moment de conversion : la personne vient de voir sa note, on
// démontre la valeur et on montre ce que l'essai débloque.
// ═══════════════════════════════════════════════════════════════
export const FREE_STEPS = [
  {
    title: 'Ton analyse est prête',
    text: "Ton compte est noté, tes formats t'attendent. 30 secondes pour voir comment t'en servir.",
  },
  {
    target: '[data-tour="score"]',
    title: 'Ta note sur 100',
    text: "Où tu es fort, où tu perds des vues. Objectif : la faire monter.",
    skipIfMissing: true,
  },
  {
    target: '[data-tour="score-improve"]',
    title: 'Et pour la faire monter ?',
    text: "Ce bouton te donne le plan complet : ce qui te coûte des vues, quoi corriger, axe par axe. Retiens-le.",
    skipIfMissing: true,
  },
  {
    target: '[data-tour="free-formats"]',
    layout: 'top',
    allowScroll: true,
    title: 'Tes 3 formats offerts',
    text: "Des formats qui marchent déjà dans ta niche. Chaque carte s'ouvre sur l'analyse.",
    skipIfMissing: true,
  },
  openFicheStep,
  persoInFicheStep("Et c'est ICI, en bas de chaque fiche, que l'essai change tout : Merlin réécrit le format pour TON compte, le hook exact à dire, les étapes à filmer. Ça donne ça :"),
  analysisDemoStep("On est dans l'onglet Analyse. Avec l'essai, tu envoies une de tes vidéos et Merlin te dit pourquoi elle a marché (ou pas), et quoi corriger. Déroule l'exemple, c'est une vraie analyse :"),
  {
    title: 'À toi de jouer',
    text: "Ouvre ton 1er format. Chaque matin, le format du jour arrive par mail.",
    action: 'open-first-format',
    actionLabel: 'Voir mes formats →',
    last: true,
    onEnter: tourCleanup,
  },
]

export default function StudioOnboarding({ onOpenFirstFormat, steps = STUDIO_STEPS, storageKey = ONBOARDED_KEY }) {
  const [show, setShow] = useState(false)

  // 1er passage uniquement (flag localStorage).
  useEffect(() => {
    if (typeof window === 'undefined') return
    // ── TUTO APRÈS CRÉATION DE COMPTE UNIQUEMENT (décision Antonin 14/07) ──
    // Le tour ne se lance JAMAIS pour un visiteur sans compte (il recouvrait
    // le tunnel note → améliorer → paywall au moment du réflexe : mesure
    // 09/07, clic améliorer 15% avec tuto vs 38% sans). Garde structurelle
    // via le flag AuthContext (__STUDIO_HAS_USER) : peu importe où le
    // composant est monté aujourd'hui ou demain, sans compte = pas de tour.
    // ?tuto=1 reste dispo pour les démos ; l'A/B timing (immédiat vs 3 min)
    // continue sur les comptes créés.
    let forced = false
    try { forced = new URLSearchParams(window.location.search).get('tuto') === '1' } catch {}
    const hasUser = (() => { try { return window.__STUDIO_HAS_USER === true } catch { return false } })()
    if (!forced && !hasUser) return
    // ── TUTO RÉSERVÉ AUX ESSAIS/ABONNÉS (décision Antonin 14/07, Telegram) ──
    // Constat : le tuto n'aide pas l'acquisition (A/B 10v10 non concluant,
    // couleurs non raccord avec la verticale sport) mais il garde sa valeur
    // pour montrer le produit à ceux qui VIENNENT de payer l'essai. Donc :
    // plan studio_* (trial ou payant) = tuto ; free = jamais. ?tuto=1 force.
    const plan = (() => { try { return window.__STUDIO_PLAN || null } catch { return null } })()
    const entitled = typeof plan === 'string' && plan.startsWith('studio_')
    if (!forced && !entitled) return
    let already = false
    try { already = localStorage.getItem(storageKey) === '1' } catch {}
    if (already && !forced) return
    // ── A/B TIMING DU TUTO (demande Antonin 09/07) ──
    // Le clic "améliorer mon compte" est un réflexe des 2 premières minutes
    // (médiane 30 s) et le tuto immédiat le divisait par 2,5. Test :
    //   A (immediate)    : tuto dès que le contenu est prêt (comportement actuel)
    //   B (delayed_3min) : mêmes étapes, mais 3 minutes après que la note est
    //                      prête — l'impulsion "améliorer" a sa fenêtre libre.
    // Split sticky 50/50 par visiteur (persisté), variante dans chaque event.
    let tutoVariant = null
    try {
      tutoVariant = localStorage.getItem('studio_tuto_timing_variant')
      if (tutoVariant !== 'immediate' && tutoVariant !== 'delayed_3min') {
        tutoVariant = Math.random() < 0.5 ? 'immediate' : 'delayed_3min'
        localStorage.setItem('studio_tuto_timing_variant', tutoVariant)
      }
    } catch { tutoVariant = 'immediate' }
    const delayMs = tutoVariant === 'delayed_3min' ? 3 * 60 * 1000 : 900

    // ATTEND du CONTENU RÉEL, pas juste les ancres : les data-tour existent
    // dès le premier render (autour des squelettes), donc tester leur
    // présence lançait le tour pendant le chargement (retour Antonin
    // 2026-07-07). Prêt = une carte format rendue OU la note chiffrée
    // affichée, puis la pose de la variante (900 ms ou 3 min) pour lancer.
    // Plafond 20 s : on lance quand même (skipIfMissing adapte les étapes).
    let tries = 0
    let launchTimer = null
    const launch = () => {
      setShow(true)
      try { studioEvents.onboardingShown({ timing_variant: tutoVariant }) } catch {}
      markActivationStep('score')
    }
    const iv = setInterval(() => {
      tries++
      const card = document.querySelector('[data-tour="formats"] article, [data-tour="free-formats"] article')
      const scoreEl = document.querySelector('[data-tour="score"]')
      const scoreReady = scoreEl && /\d/.test(scoreEl.textContent || '')
      if (card || scoreReady) {
        clearInterval(iv)
        launchTimer = setTimeout(launch, delayMs)
      } else if (tries > 40) {
        clearInterval(iv)
        launchTimer = setTimeout(launch, tutoVariant === 'delayed_3min' ? delayMs : 0)
      }
    }, 500)
    return () => { clearInterval(iv); if (launchTimer) clearTimeout(launchTimer) }
  }, [])

  // Relance manuelle (bouton "Revoir le tutoriel") : on remet le tour a zero.
  useEffect(() => {
    function onRestart() {
      try {
        localStorage.removeItem(storageKey)
        localStorage.removeItem(ACTIVATION_KEY)
        localStorage.removeItem(COLLAPSED_KEY)
        localStorage.removeItem('studio_activation_done_dismissed')
      } catch {}
      try { studioEvents.onboardingShown() } catch {}
      markActivationStep('score')
      setShow(true)
    }
    window.addEventListener('studio:restart-tutorial', onRestart)
    return () => window.removeEventListener('studio:restart-tutorial', onRestart)
  }, [])

  const persist = useCallback(() => {
    try { localStorage.setItem(storageKey, '1') } catch {}
  }, [storageKey])

  const finish = useCallback(() => {
    persist()
    setShow(false)
    tourCleanup() // referme fiche + démo si on quitte en cours de route
    try { studioEvents.onboardingCompleted() } catch {}
  }, [persist])

  const onAction = useCallback((action) => {
    if (action !== 'open-first-format') return
    persist()
    try { studioEvents.onboardingCtaOpenFormat() } catch {}
    // Laisse le tour se demonter avant d'ouvrir (scroll-into-view propre).
    setTimeout(() => {
      if (typeof onOpenFirstFormat === 'function') onOpenFirstFormat()
      else { try { window.dispatchEvent(new CustomEvent('studio:open-first-format')) } catch {} }
    }, 120)
  }, [onOpenFirstFormat, persist])

  if (!show) return null
  return <SpotlightTutorial steps={steps} onFinish={finish} onAction={onAction}
    onStepChange={(i, st) => { try { studioEvents.onboardingStep({ step_index: i, step_title: st.title }) } catch { /* tracking */ } }} />
}

// ═══════════════════════════════════════════════════════════════
// CHECKLIST D'ACTIVATION — compacte, en haut de la home.
// Coche au fil de l'eau. Encourageante, pas culpabilisante. 4 etapes
// orientees SURFACES (chaque CTA emmene l'user AGIR sur une surface cle).
// Repliable (chevron) + disparait quand les 4 etapes sont faites.
// ═══════════════════════════════════════════════════════════════
// Ordre abonnés (Antonin 23/07) : l'analyse vidéo JUSTE après la note —
// c'est la fonctionnalité miroir la plus sous-utilisée (37%), on pousse
// les pros à analyser une vidéo d'entrée de jeu.
const STEPS = [
  { key: 'score',   label: 'Note /100' },
  { key: 'video',   label: 'Analyse une vidéo', cta: 'Analyser →', event: 'studio:goto-video-analysis' },
  { key: 'format',  label: 'Ouvre un format',   cta: 'Voir →',     event: 'studio:goto-library' },
  { key: 'account', label: 'Plan d\'action',    cta: 'Voir →',     event: 'studio:goto-account-analysis' },
]

// Variante NON-ABONNÉS (décision Antonin 23/07) : 3 étapes accessibles
// gratuitement, et la complétion débloque la récompense — l'analyse
// complète de SA dernière vidéo, lancée automatiquement côté serveur.
// 4 actions types, pas de gamification (décision Antonin 23/07 : « on
// enlève l'histoire du coaching, juste 4 actions types qui débloquent
// le cadeau »)
const STEPS_GRATUIT = [
  { key: 'score',  label: 'Note /100' },
  { key: 'format', label: 'Ouvre un format',      cta: 'Voir →',     event: 'studio:goto-library' },
  { key: 'perso',  label: 'Tes exemples perso',   cta: 'Générer →',  event: 'studio:goto-library' },
  { key: 'save',   label: 'Sauvegarde un format', cta: 'Choisir →',  event: 'studio:goto-library' },
]
const REWARD_CLAIMED_KEY = 'studio_activation_reward_claimed'


export function ActivationChecklist({ onOpenFirstFormat }) {
  // Variante selon l'abonnement : abonnés/essais = 4 étapes classiques ;
  // non-abonnés = 3 étapes gratuites + récompense analyse vidéo offerte.
  const auth = useAuth()
  const subscriber = ['pro', 'pro_plus'].includes(auth?.studioPlan)
  const ACTIVE_STEPS = subscriber ? STEPS : STEPS_GRATUIT
  const [rewardState, setRewardState] = useState(() => {
    try { return localStorage.getItem(REWARD_CLAIMED_KEY) || '' } catch { return '' }
  })
  const [claiming, setClaiming] = useState(false)
  async function claimReward() {
    if (claiming || rewardState) return
    setClaiming(true)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      const r = await fetch(`${API_URL}/api/studio/activation-reward`, {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const out = await r.json()
      if (out.id || out.already) {
        setRewardState(out.id || 'ok')
        try { localStorage.setItem(REWARD_CLAIMED_KEY, out.id || 'ok') } catch { /* no-op */ }
        try { studioEvents.tabClicked?.({ tab: 'activation_reward_claimed', source_page: 'checklist' }) } catch { /* tracking */ }
        try { window.dispatchEvent(new CustomEvent('studio:goto-video-analysis')) } catch { /* nav */ }
      }
    } catch { /* réessayable au prochain clic */ }
    setClaiming(false)
  }
  const [state, setState] = useState(() => readActivation())
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem('studio_activation_done_dismissed') === '1' } catch { return false }
  })
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1' } catch { return false }
  })
  const justCompletedRef = useRef(false)

  useEffect(() => {
    function onChange(e) {
      const next = e?.detail || readActivation()
      setState({ score: !!next.score, format: !!next.format, video: !!next.video, account: !!next.account, perso: !!next.perso, save: !!next.save })
    }
    function onStorage(e) {
      if (e.key === ACTIVATION_KEY) setState(readActivation())
    }
    // L'ouverture d'un format (event direct OU changement de saves) coche 'format'.
    function onFormatStep() { markActivationStep('format') }
    function onVideoStep() { markActivationStep('video') }
    function onAccountStep() { markActivationStep('account') }
    function onSaveStep() { markActivationStep('save') }
    window.addEventListener('studio:activation-changed', onChange)
    window.addEventListener('storage', onStorage)
    window.addEventListener('studio:saves-changed', onFormatStep)
    window.addEventListener('studio:format-opened', onFormatStep)
    window.addEventListener('studio:video-analysis-started', onVideoStep)
    window.addEventListener('studio:account-analysis-started', onAccountStep)
    function onPersoStep() { markActivationStep('perso') }
    window.addEventListener('studio:examples-generated', onPersoStep)
    window.addEventListener('studio:saves-changed', onSaveStep)
    return () => {
      window.removeEventListener('studio:examples-generated', onPersoStep)
      window.removeEventListener('studio:saves-changed', onSaveStep)
      window.removeEventListener('studio:activation-changed', onChange)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('studio:saves-changed', onFormatStep)
      window.removeEventListener('studio:format-opened', onFormatStep)
      window.removeEventListener('studio:video-analysis-started', onVideoStep)
      window.removeEventListener('studio:account-analysis-started', onAccountStep)
    }
  }, [])

  const done = ACTIVE_STEPS.filter(s => state[s.key]).length
  const total = ACTIVE_STEPS.length
  const allDone = done === total
  const pct = Math.round((done / total) * 100)

  useEffect(() => {
    if (allDone && !justCompletedRef.current) justCompletedRef.current = true
  }, [allDone])

  function toggleCollapsed(next) {
    setCollapsed(next)
    try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0') } catch {}
  }

  if (hidden) return null

  // Etape 'format' : on emmene d'abord sur Decouvrir, puis on ouvre le 1er format.
  function handleStepCta(step) {
    if (step.key === 'format') {
      try { window.dispatchEvent(new CustomEvent('studio:goto-library')) } catch {}
      if (typeof onOpenFirstFormat === 'function') onOpenFirstFormat()
      else { try { window.dispatchEvent(new CustomEvent('studio:open-first-format')) } catch {} }
      return
    }
    if (step.event) { try { window.dispatchEvent(new CustomEvent(step.event)) } catch {} }
  }

  // Etat REPLIE : barre fine cliquable qui rouvre le guide.
  if (collapsed) {
    return (
      <button
        onClick={() => toggleCollapsed(false)}
        className="w-full rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 border transition-all hover:scale-[1.005]"
        style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.06), rgba(245,158,11,0.02))', borderColor: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.20)' }}>
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-base">{subscriber ? '🪄' : '🎁'}</span>
          <span className="text-[13px] font-bold text-ink truncate">{subscriber ? 'Bien démarrer' : '4 actions, 1 cadeau : ton analyse vidéo offerte'}</span>
        </span>
        <span className="shrink-0 flex items-center gap-2">
          <span className="text-[12px] font-bold" style={{ color: 'var(--merlin-accent, #fe8204)' }}>{done}/{total}</span>
          <span className="text-ink-faint text-sm leading-none">⌄</span>
        </span>
      </button>
    )
  }

  return (
    <div
      className="rounded-2xl px-3.5 py-3 border"
      style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.06), rgba(245,158,11,0.02))', borderColor: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.20)' }}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[10px] uppercase tracking-wider font-bold text-orange truncate">{subscriber ? 'Bien démarrer' : '4 actions, 1 cadeau : ton analyse vidéo offerte'}</p>
        <div className="shrink-0 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-extrabold"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: '#d97206', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
            {done}/{total}
          </span>
          <button
            onClick={() => toggleCollapsed(true)}
            className="text-ink-faint hover:text-ink leading-none p-0.5"
            aria-label="Cacher le guide" title="Cacher le guide">
            <span className="text-sm">⌃</span>
          </button>
        </div>
      </div>

      {/* FRISE : les 4 étapes, et pour les non-abonnés les pointillés
          mènent au cadeau. Rien après la 4e étape pour les abonnés
          (demande Antonin 23/07 : le tuto s'arrête à l'étape 4). */}
      <div className="flex items-start">
        {ACTIVE_STEPS.map((step, i) => {
          const checked = !!state[step.key]
          const isCurrent = !checked && ACTIVE_STEPS.slice(0, i).every(st => state[st.key])
          return (
            <div key={step.key} className="flex-1 min-w-0 flex flex-col items-center relative">
              {i < ACTIVE_STEPS.length - 1 && (
                <span className="absolute top-[11px] left-1/2 w-full h-[2px]"
                  style={{ background: checked ? '#16a34a' : 'rgba(15,10,26,0.10)' }} />
              )}
              {i === ACTIVE_STEPS.length - 1 && !subscriber && (
                <span className="absolute top-[11px] left-1/2 w-full h-0 border-t-2 border-dotted"
                  style={{ borderColor: checked ? 'rgba(22,163,74,0.5)' : 'rgba(15,10,26,0.15)' }} />
              )}
              <button type="button"
                onClick={() => !checked && handleStepCta(step)}
                className="relative z-[1] w-[22px] h-[22px] rounded-full flex items-center justify-center transition-transform hover:scale-110"
                style={{
                  background: checked ? '#16a34a' : isCurrent ? 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' : 'white',
                  border: checked || isCurrent ? 'none' : '1.5px solid rgba(15,10,26,0.14)',
                  boxShadow: isCurrent ? '0 2px 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' : 'none',
                }}
                aria-label={step.label}>
                <span className={`text-[10.5px] font-bold ${checked || isCurrent ? 'text-white' : 'text-ink-faint'}`}>
                  {checked ? '✓' : i + 1}
                </span>
              </button>
              <p className={`mt-1 text-center text-[9.5px] leading-tight px-0.5 whitespace-nowrap overflow-hidden text-ellipsis w-full ${checked ? 'text-ink-faint' : isCurrent ? 'text-ink font-bold' : 'text-ink-muted'}`}>
                {step.label}
              </p>
            </div>
          )
        })}
        {/* RÉCOMPENSE non-abonnés : l'analyse vidéo offerte (sa dernière
            vidéo, lancée automatiquement côté serveur) — remplace le jalon
            quotidien jusqu'à réclamation (décision Antonin 23/07) */}
        {!subscriber && !rewardState && (
          <div className="flex-1 min-w-0 flex flex-col items-center relative">
            <span className="absolute top-[11px] left-1/2 w-full h-0 border-t-2 border-dotted" style={{ borderColor: allDone ? 'rgba(22,163,74,0.5)' : 'rgba(15,10,26,0.15)' }} />
            <button type="button"
              onClick={() => allDone && claimReward()}
              disabled={!allDone || claiming}
              className={`relative z-[1] w-[22px] h-[22px] rounded-full flex items-center justify-center transition-transform ${allDone ? 'hover:scale-110 animate-pulse' : ''}`}
              style={{
                background: allDone ? 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))' : 'white',
                border: allDone ? 'none' : '1.5px dashed rgba(124,58,237,0.45)',
                boxShadow: allDone ? '0 2px 10px rgba(124,58,237,0.4)' : 'none',
              }}
              aria-label="Ton analyse vidéo offerte">
              <span className="text-[11px]">{claiming ? '…' : '🎁'}</span>
            </button>
            <p className={`mt-1 text-center text-[9.5px] leading-tight px-0.5 whitespace-nowrap overflow-hidden text-ellipsis w-full ${allDone ? 'font-bold' : 'text-ink-muted'}`}
              style={allDone ? { color: '#7c3aed' } : {}}>
              {allDone ? 'Récupère ton analyse !' : 'Analyse offerte'}
            </p>
          </div>
        )}
        {!subscriber && rewardState && (
          <div className="flex-1 min-w-0 flex flex-col items-center relative">
            <span className="absolute top-[11px] left-1/2 w-full h-0 border-t-2 border-dotted" style={{ borderColor: 'rgba(22,163,74,0.5)' }} />
            <button type="button"
              onClick={() => { try { window.dispatchEvent(new CustomEvent('studio:goto-video-analysis')) } catch { /* nav */ } }}
              className="relative z-[1] w-[22px] h-[22px] rounded-full flex items-center justify-center bg-green-600 hover:scale-110 transition-transform"
              aria-label="Voir mon analyse offerte">
              <span className="text-[10.5px] font-bold text-white">✓</span>
            </button>
            <p className="mt-1 text-center text-[9.5px] leading-tight px-0.5 whitespace-nowrap overflow-hidden text-ellipsis w-full text-green-700 font-bold">
              Analyse lancée 🎬
            </p>
          </div>
        )}
      </div>

      {/* Le CTA du moment : l'étape en cours, ou la récompense côté gratuit */}
      {(() => {
        const current = ACTIVE_STEPS.find((st, i) => !state[st.key] && ACTIVE_STEPS.slice(0, i).every(x => state[x.key]))
        if (current && current.cta) return (
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-[11px] text-ink-muted leading-tight min-w-0 truncate">{subscriber ? 'Tes premières actions sur Studio' : 'Termine les 4 actions, ton analyse complète t\u2019attend'}</p>
            <button
              onClick={() => handleStepCta(current)}
              className="shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-lg text-white transition-all hover:scale-[1.03]"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 2px 6px rgba(var(--merlin-accent-rgb, 254,130,4),0.28)' }}>
              {current.label} →
            </button>
          </div>
        )
        if (!subscriber && allDone && !rewardState) return (
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-[11px] font-bold text-ink leading-tight min-w-0 truncate">Tout est fait !</p>
            <button
              onClick={claimReward}
              disabled={claiming}
              className="shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-lg text-white transition-all hover:scale-[1.03] disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))', boxShadow: '0 2px 8px rgba(124,58,237,0.35)' }}>
              {claiming ? 'Lancement…' : '🎁 Récupérer mon analyse offerte'}
            </button>
          </div>
        )
        return null
      })()}
    </div>
  )
}

